import { PropsWithChildren, useMemo, useContext } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Link, Outlet, useLocation } from 'react-router-dom';
import SeoProjectContext from '@/contexts/seoProjectContext';
import { SeoProject } from '@/types/api/seo';

type Props = PropsWithChildren<{
  project?: SeoProject;
}>;

const sectionLabels: Record<string, string> = {
  audits: 'Site Audit',
  keywords: 'Keywords',
  pages: 'Pages',
  links: 'Internal Links',
  copilot: 'SEO Copilot',
  tasks: 'Tasks',
  automation: 'Automation',
  changelog: 'Changelog',
  content: 'Content',
  reports: 'Reports',
  settings: 'Settings',
};

const SEOxProject = ({ project, children }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const { breadcrumbDetail } = useContext(SeoProjectContext);

  const breadcrumbs = useMemo(() => {
    if (!project) return [];

    const base = `/seox/projects/${project.shortId}`;
    const sub = pathname.replace(base, '').replace(/^\//, '');
    const parts = sub.split('/').filter(Boolean);
    const section = parts[0];
    const hasDetail = parts.length > 1;

    const crumbs: { label: string; to?: string }[] = [
      { label: 'Projects', to: '/seox' },
      { label: project.name, to: section ? base : undefined },
    ];

    if (section && sectionLabels[section]) {
      if (hasDetail) {
        crumbs.push({ label: sectionLabels[section], to: `${base}/${section}` });
        if (breadcrumbDetail) {
          crumbs.push({ label: breadcrumbDetail });
        }
      } else {
        crumbs.push({ label: sectionLabels[section] });
      }
    }

    return crumbs;
  }, [project, pathname, breadcrumbDetail]);

  if (!project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#054AD9' }} />
      </Box>
    );
  }

  return (
    <>
      {breadcrumbs.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            pb: 2,
          }}
        >
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {i > 0 && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: 11, userSelect: 'none' }}
                  >
                    /
                  </Typography>
                )}
                {crumb.to && !isLast ? (
                  <Link to={crumb.to} style={{ textDecoration: 'none' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#71717A',
                        fontSize: 13,
                        fontWeight: 400,
                        transition: 'color 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                        '&:hover': { color: '#054AD9' },
                      }}
                    >
                      {crumb.label}
                    </Typography>
                  </Link>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#09090B',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {crumb.label}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <Outlet />
      {children}
    </>
  );
};

export default SEOxProject;
